import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr, API } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import CatalogueModal, { downloadPdf } from "@/components/CatalogueModal";
import CatalogueBuilder from "./CatalogueBuilder";
import { useCredits } from "@/hooks/useCredits";
import { Spinner, Overline } from "@/components/Brand";
import { toast } from "sonner";
import { FileText, Trash2, Share2, Link2, Boxes, RefreshCw, Plus, Pencil, Download, Eye } from "lucide-react";

function StatusBadge({ c }) {
  if (c.type === "pdf") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-400">Legacy PDF</span>;
  if (c.status === "draft") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-500" data-testid={`status-${c.id}`}>Draft</span>;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.has_unpublished_changes ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/10 text-emerald-500"}`} data-testid={`status-${c.id}`}>
      {c.has_unpublished_changes ? "Unpublished changes" : "Published"}
    </span>
  );
}

function CatRow({ c, actions }) {
  const cover = c.ai_content?.cover_image || c.ai_content?.products?.[0]?.image;
  const isLive = c.type === "pdf" || c.status !== "draft";
  const chip = "text-[11px] px-2.5 py-1 rounded-full glass text-slate-300 border border-amber-500/20 flex items-center gap-1";
  return (
    <div className="glass rounded-2xl p-4" data-testid={`ex-catalogue-${c.id}`}>
      <div className="flex items-center gap-3">
        {cover ? <img src={`${API}/files/${cover}`} alt="" className="h-14 w-14 rounded-xl object-cover shrink-0" />
          : <div className="h-14 w-14 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0"><FileText size={18} className="text-amber-400" /></div>}
        <div className="flex-1 min-w-0">
          <div className="text-slate-100 font-medium truncate">{c.title}</div>
          <div className="flex items-center gap-2 mt-1"><StatusBadge c={c} />{c.type === "ai" && <span className="text-[11px] text-slate-500">{(c.ai_content?.products || []).length} products</span>}</div>
        </div>
        {c.type === "ai" && <button onClick={actions.edit} className="h-9 w-9 rounded-lg glass flex items-center justify-center text-slate-300" data-testid={`edit-cat-${c.id}`}><Pencil size={15} /></button>}
        <button onClick={actions.del} className="h-9 w-9 rounded-lg glass flex items-center justify-center text-red-300" data-testid={`del-cat-${c.id}`}><Trash2 size={15} /></button>
      </div>
      {isLive && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-amber-500/10">
          {c.type === "ai" && <button onClick={actions.view} className={chip} data-testid={`ex-view-ai-${c.id}`}><Eye size={12} /> View</button>}
          {c.type === "pdf" && c.storage_path && <a href={`${API}/files/${c.storage_path}`} target="_blank" rel="noreferrer" className={chip} data-testid={`open-pdf-${c.id}`}><FileText size={12} /> Open PDF</a>}
          {c.type === "ai" && <button onClick={actions.pdf} className={chip} data-testid={`pdf-${c.id}`}><Download size={12} /> PDF</button>}
          <button onClick={actions.share} className={chip} data-testid={`share-${c.id}`}><Share2 size={12} /> Share</button>
          <button onClick={actions.secret} className={chip} data-testid={`secret-link-${c.id}`}><Link2 size={12} /> Token link</button>
          <select value={c.access_policy || "registered"} onChange={(e) => actions.policy(e.target.value)} className="bg-black/30 gold-border rounded-lg px-2 py-1 text-xs text-slate-100 focus:outline-none" data-testid={`policy-${c.id}`}>
            <option value="public">Anyone with link</option>
            <option value="registered">Registered visitors</option>
            <option value="members">Members</option>
            <option value="trade">Trade visitors only</option>
          </select>
          <button onClick={actions.copy} className={chip} data-testid={`copy-edition-${c.id}`}>Copy to current edition</button>
          <button onClick={actions.revoke} className={`${chip} text-red-300`} data-testid={`revoke-${c.id}`}><RefreshCw size={12} /> Revoke</button>
        </div>
      )}
    </div>
  );
}

export default function ExhibitorCatalogues() {
  const [cats, setCats] = useState(null);
  const [viewCat, setViewCat] = useState(null);
  const [builder, setBuilder] = useState(undefined);
  const { credits, setCredits, costs } = useCredits();

  const load = async () => { const { data } = await api.get("/exhibitor/catalogues"); setCats(data); };
  useEffect(() => { load(); }, []);

  const call = async (fn, msg) => { try { await fn(); if (msg) { toast.success(msg); } load(); } catch (err) { toast.error(apiErr(err)); } };
  const actionsFor = (c) => ({
    edit: () => setBuilder(c.id),
    view: () => setViewCat(c),
    pdf: () => downloadPdf(`/exhibitor/catalogues/${c.id}/pdf`, c.title),
    del: () => window.confirm("Delete this catalogue? Its share links will stop working.") && call(() => api.delete(`/exhibitor/catalogues/${c.id}`), "Removed"),
    share: () => call(async () => {
      const { data } = await api.post(`/exhibitor/share-catalogue/${c.id}`);
      await navigator.clipboard?.writeText(data.link).catch(() => {});
    }, "Link copied"),
    secret: async () => { await navigator.clipboard?.writeText(`${window.location.origin}/share/t/${c.share_token}`).catch(() => {}); toast.success("Secret token link copied"); },
    policy: (v) => call(() => api.put(`/exhibitor/catalogues/${c.id}/policy`, { access_policy: v }), "Access updated"),
    copy: () => call(() => api.post(`/exhibitor/catalogues/${c.id}/copy-to-edition`), "Copied to current edition"),
    revoke: () => call(() => api.post(`/exhibitor/catalogues/${c.id}/revoke-link`), "Old links revoked · new token issued"),
  });

  return (
    <Shell nav={exhibitorNav}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <Overline>Catalogues</Overline>
          <h1 className="font-display text-3xl gold-text mt-2">Your Collections</h1>
          <p className="text-slate-400 text-sm mt-1">Build catalogues from My Products. Save drafts, preview, then publish. <span className="text-amber-300" data-testid="catalogue-credits">{credits} credits</span></p>
        </div>
        <div className="flex gap-2">
          <Link to="/exhibitor/products" className="glass px-4 py-2.5 rounded-xl text-sm text-slate-200 flex items-center gap-2 border border-amber-500/20" data-testid="product-library-link"><Boxes size={15} className="text-amber-400" /> My Products</Link>
          <button onClick={() => setBuilder(null)} className="btn-gold px-4 py-2.5 rounded-xl flex items-center gap-2" data-testid="assemble-catalogue-button"><Plus size={16} /> New Catalogue</button>
        </div>
      </div>

      {!cats ? <Spinner /> : cats.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center mt-6" data-testid="catalogues-empty"><p className="text-slate-400 text-sm">No catalogues yet. Create your first one from My Products.</p></div>
      ) : (
        <div className="space-y-3 mt-6">{cats.map((c) => <CatRow key={c.id} c={c} actions={actionsFor(c)} />)}</div>
      )}

      {viewCat && <CatalogueModal catalogue={viewCat} onClose={() => setViewCat(null)} />}
      {builder !== undefined && (
        <CatalogueBuilder catalogueId={builder} credits={credits} setCredits={setCredits} costs={costs}
          onClose={() => { setBuilder(undefined); load(); }} onSaved={load} />
      )}
    </Shell>
  );
}
